import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { GoogleMap, Marker, InfoWindow } from '@react-google-maps/api';
import { Map as MapIcon, X, Search as SearchIcon, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useGoogleMaps } from '../../lib/googleMaps';

const formatINR = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

// Convert hotels[] → {lat, lng}[] (Google Maps shape)
const useMarkers = (hotels) =>
  useMemo(
    () =>
      (hotels || [])
        .filter((h) => h?.coordinates?.latitude && h?.coordinates?.longitude)
        .slice(0, 60)
        .map((h) => ({
          id: h.id,
          name: h.name,
          area: h.area,
          price: h.display_price ?? h.net_price_per_night,
          position: { lat: h.coordinates.latitude, lng: h.coordinates.longitude },
        })),
    [hotels],
  );

const HotelMap = ({ hotels, city, onClose, fullScreen = false }) => {
  const { isLoaded, loadError } = useGoogleMaps();
  const markers = useMarkers(hotels);
  const [activeId, setActiveId] = useState(null);

  const center = useMemo(() => {
    if (markers.length === 0) return { lat: 22.5, lng: 78.9 };
    const lat = markers.reduce((s, m) => s + m.position.lat, 0) / markers.length;
    const lng = markers.reduce((s, m) => s + m.position.lng, 0) / markers.length;
    return { lat, lng };
  }, [markers]);

  if (loadError) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-center">
        <MapIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
        <p className="text-sm text-red-600">Failed to load Google Maps.</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className={`bg-gray-100 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-800 ${fullScreen ? 'h-full' : 'h-[160px]'} animate-pulse flex items-center justify-center`}>
        <MapIcon className="h-6 w-6 text-gray-400 animate-pulse" />
      </div>
    );
  }

  if (markers.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-center">
        <MapIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
        <p className="text-sm text-gray-500">Map view not available for these results.</p>
      </div>
    );
  }

  const containerStyle = { width: '100%', height: fullScreen ? '100%' : '160px' };

  return (
    <div className={`relative bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden shadow-sm ${fullScreen ? 'h-full' : ''}`} data-testid="hotel-map">
      {fullScreen && onClose && (
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-[401] bg-white shadow-md rounded-full p-2 hover:bg-gray-50"
          data-testid="close-fullscreen-map"
        >
          <X className="h-4 w-4 text-gray-700" />
        </button>
      )}
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={markers.length > 1 ? 12 : 14}
        options={{
          disableDefaultUI: !fullScreen,
          zoomControl: true,
          gestureHandling: fullScreen ? 'auto' : 'cooperative',
          clickableIcons: false,
          styles: [{ featureType: 'poi.business', stylers: [{ visibility: 'off' }] }],
        }}
      >
        {markers.map((m) => (
          <Marker
            key={m.id}
            position={m.position}
            onClick={() => setActiveId(m.id)}
            label={fullScreen ? { text: formatINR(m.price), color: '#fff', fontWeight: '700', fontSize: '11px' } : undefined}
            icon={{
              path: window.google?.maps?.SymbolPath?.CIRCLE,
              scale: 9,
              fillColor: '#dc2626',
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 2,
            }}
          >
            {activeId === m.id && (
              <InfoWindow position={m.position} onCloseClick={() => setActiveId(null)}>
                <div className="text-xs">
                  <p className="font-bold text-sm mb-0.5 text-gray-900">{m.name}</p>
                  {m.area && <p className="text-gray-500">{m.area}</p>}
                  <p className="font-bold text-red-600 mt-1">{formatINR(m.price)}</p>
                  <p className="text-[10px] text-gray-500">per night</p>
                </div>
              </InfoWindow>
            )}
          </Marker>
        ))}
      </GoogleMap>

      {!fullScreen && (
        <div className="absolute top-3 left-3 right-3 z-10 bg-white/95 backdrop-blur px-3 py-2 rounded-lg shadow text-xs font-semibold text-gray-900 pointer-events-none">
          {markers.length} hotels in {city || 'this area'}
        </div>
      )}
    </div>
  );
};

export const HotelMapSidebar = ({ hotels, city, onLocalitySearch }) => {
  const [open, setOpen] = useState(false);
  const [locality, setLocality] = useState('');

  // ESC key closes the fullscreen map.
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    // Lock body scroll while modal is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  return (
    <>
      {/* MMT-style "Explore on Map" preview — uses a static map image as the background */}
      <div
        className="relative rounded-2xl overflow-hidden cursor-pointer group bg-gray-200 dark:bg-gray-800 shadow-sm hover:shadow-md transition-shadow h-[170px]"
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); } }}
        data-testid="open-fullscreen-map-btn"
      >
        {/* Static map background — image provided by user */}
        <img
          src="/map-preview.jpg"
          alt="Map preview"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-white/15 dark:bg-black/25" />

        {/* Centered "EXPLORE ON MAP" pill — thin black border, black text */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-900 border border-gray-900 dark:border-gray-200 rounded-full px-5 py-2.5 shadow-md flex items-center gap-2 group-hover:scale-105 transition-transform">
            <span className="text-gray-900 dark:text-white font-extrabold text-sm tracking-wide uppercase">Explore on Map</span>
            <MapPin className="h-4 w-4 text-gray-900 dark:text-white" />
          </div>
        </div>
      </div>

      {/* Locality quick-search (kept as a small input under the preview) */}
      <div className="mt-3 px-1">
        <div className="relative">
          <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input
            data-testid="locality-search-input"
            type="text"
            value={locality}
            placeholder="Search locality (e.g., Andheri)"
            onChange={(e) => setLocality(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && onLocalitySearch && locality.trim()) {
                onLocalitySearch(locality.trim());
              }
            }}
            className="h-9 pl-8 text-xs bg-white dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700"
          />
        </div>
      </div>

      {open && createPortal(
        <div className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="map-modal-backdrop">
          <div className="w-full max-w-5xl h-[80vh] bg-white dark:bg-gray-900 rounded-xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <HotelMap hotels={hotels} city={city} onClose={() => setOpen(false)} fullScreen />
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default HotelMap;
